import React, { useRef } from 'react';
import Input from '@salesforce/design-system-react/components/input';
import { apiResetPassword } from '../../../../services/auth';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from '@salesforce/design-system-react';
import { toast } from 'react-hot-toast';

const validationSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Enter your current Password'),
  newPassword: Yup.string()
    .required('Enter your new password')
    .min(8, 'New password must be at least 8 characters')
    .notOneOf([Yup.ref('currentPassword')], 'Current password and new password cannot be same'),
  confirmPassword: Yup.string()
    .required('Enter your confirm password')
    .oneOf([Yup.ref('newPassword'), null], 'Confirm password and new password must match'),
});

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const Details = ({ account }) => {
  const formikRef = useRef();
  const onFormSubmit = async (values, setSubmitting, resetForm) => {
    const result = await apiResetPassword(values);
    if (result.data.success) {
      toast.success(result.data.message, {
        id: 'change-password-success-toast',
      });
      resetForm();
    } else {
      toast.error(result.data.message, {
        id: 'change-password-error-toast',
      });
    }
    setSubmitting(false);
  };

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize={true}
      initialValues={{
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        setSubmitting(true);
        onFormSubmit(values, setSubmitting, resetForm);
      }}
    >
      {(props) => {
        const {
          values,
          touched,
          errors,
          isSubmitting,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        } = props;
        return (
          <form
            onSubmit={handleSubmit}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSubmit();
              }
            }}
          >
            <IconSettings iconPath="/assets/icons">
              <div className="add-form">
                <section className="slds-p-around_large">
                  <div className="slds-col_padded slds-m-bottom_large">
                    <Input
                      id="currentPassword"
                      name="currentPassword"
                      label="Current Password"
                      placeholder=""
                      required
                      type="password"
                      value={values.currentPassword || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={classNames(
                        errors.currentPassword && touched.currentPassword && 'slds-has-error'
                      )}
                    />
                    {errors.currentPassword && touched.currentPassword && (
                      <div className="slds-form-element__help">{errors.currentPassword}</div>
                    )}
                  </div>

                  <div className="slds-col_padded slds-m-bottom_large">
                    <Input
                      id="newPassword"
                      name="newPassword"
                      label="New Password"
                      placeholder=""
                      required
                      type="password"
                      value={values.newPassword || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={classNames(
                        errors.newPassword && touched.newPassword && 'slds-has-error'
                      )}
                    />
                    {errors.newPassword && touched.newPassword && (
                      <div className="slds-form-element__help">{errors.newPassword}</div>
                    )}
                  </div>

                  <div className="slds-col_padded slds-m-bottom_large">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      label="Confirm Password"
                      placeholder=""
                      required
                      type="password"
                      value={values.confirmPassword || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={classNames(
                        errors.confirmPassword && touched.confirmPassword && 'slds-has-error'
                      )}
                    />
                    {errors.confirmPassword && touched.confirmPassword && (
                      <div className="slds-form-element__help">{errors.confirmPassword}</div>
                    )}
                  </div>

                  <div style={{ paddingLeft: '10px' }}>
                    <Button
                      label={isSubmitting ? <span>Loading</span> : <span>Change password</span>}
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                      type="submit"
                      variant="brand"
                    />
                  </div>
                </section>
              </div>
            </IconSettings>
          </form>
        );
      }}
    </Formik>
  );
};

export default Details;
